import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  UserPlus,
  Wallet,
  Tag,
  Check,
  X,
  Loader2,
  AlertCircle,
  AlertTriangle,
  ExternalLink,
  ArrowDownCircle,
  ArrowUpCircle,
  Trash2,
  FileText,
  FilePlus2,
  FileSignature,
  Download,
  Scale,
  ListTodo,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  apiClient,
  type AssistantProposedAction,
  type AssistantConfirmActionResponse,
  type AssistantCreateClientPreview,
  type AssistantFinancialEntryPreview,
  type AssistantFinancialCategoryPreview,
  type AssistantDeleteFinancialEntryPreview,
  type AssistantGenerateDocumentPreview,
  type AssistantGeneratedDocumentResult,
  type AssistantCreateDocumentTemplatePreview,
  type AssistantCreateCasePreview,
  type AssistantCreateTaskPreview,
  type ApiError,
} from '@/lib/api-client';

interface ActionPreviewCardProps {
  action: AssistantProposedAction;
}

type CardState =
  | { kind: 'idle' }
  | { kind: 'confirming' }
  | { kind: 'done'; result: AssistantConfirmActionResponse }
  | { kind: 'canceled' }
  | { kind: 'error'; message: string };

/**
 * Card de confirmacao de uma acao proposta pelo assistente (ADR-0015 P2),
 * GENERICO sobre kinds. O gate humano vive aqui, FORA do loop do modelo: a tool
 * de escrita so PROPOS a acao (com um proposalId efemero). Confirmar chama o
 * endpoint REST dedicado, que executa a escrita real (auditada, no tenant).
 * Cancelar apenas descarta no front (a proposta expira sozinha no servidor).
 *
 * Estrutura pensada para extensao (rodada 2): a casca (borda, botoes, estados
 * idle/confirming/done/error/canceled) e comum; cada kind fornece so
 * cabecalho + corpo do preview (renderPreview) e o render do estado "feito"
 * (renderDone). Adicionar um kind = mais um branch em cada um desses tres
 * pontos + o membro na uniao AssistantProposedAction.
 */
export function ActionPreviewCard({ action }: ActionPreviewCardProps) {
  const [state, setState] = useState<CardState>({ kind: 'idle' });
  // Acao destrutiva (exclusao): card inteiro em tom de alerta, para nunca ser
  // confundido visualmente com uma proposta de criacao/cadastro.
  const isDestructive = action.action === 'delete_financial_entry';

  async function confirm() {
    setState({ kind: 'confirming' });
    try {
      const result = await apiClient.assistantConfirmAction(action.proposalId);
      setState({ kind: 'done', result });
    } catch (err) {
      setState({ kind: 'error', message: describeError(err) });
    }
  }

  if (state.kind === 'done') {
    return renderDone(state.result);
  }

  if (state.kind === 'canceled') {
    return (
      <div className="rounded-lg border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-3 py-2 text-xs text-[var(--fg-muted)]">
        {CANCELED_LABEL[action.action]}
      </div>
    );
  }

  const header = HEADER[action.action];

  return (
    <div
      className={cn(
        'rounded-lg border bg-[var(--bg)] overflow-hidden',
        isDestructive
          ? 'border-red-300 dark:border-red-800'
          : 'border-[color-mix(in_oklch,var(--accent)_30%,var(--border))]',
      )}
    >
      {/* Cabecalho */}
      <div
        className={cn(
          'flex items-center gap-1.5 border-b px-3 py-2 text-xs font-semibold',
          isDestructive
            ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400'
            : 'border-[var(--border)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)] text-[var(--fg)]',
        )}
      >
        {header.icon}
        {header.title}
      </div>

      {/* Dados propostos */}
      <dl className="px-3 py-2.5 space-y-1.5 text-xs">{renderPreview(action)}</dl>

      {isDestructive && state.kind !== 'error' && (
        <div className="mx-3 mb-2 flex items-start gap-1.5 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>Esta ação não pode ser desfeita pelo usuário. Confira os dados antes de confirmar.</span>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="mx-3 mb-2 flex items-start gap-1.5 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>{state.message}</span>
        </div>
      )}

      {/* Acoes */}
      <div className="flex gap-2 border-t border-[var(--border)] px-3 py-2">
        <button
          type="button"
          onClick={confirm}
          disabled={state.kind === 'confirming'}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
            'transition-opacity hover:opacity-90 disabled:opacity-50',
            isDestructive
              ? 'bg-red-600 text-white dark:bg-red-600'
              : 'bg-[var(--accent)] text-[var(--accent-fg)]',
          )}
        >
          {state.kind === 'confirming' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : isDestructive ? (
            <Trash2 className="h-3.5 w-3.5" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          {isDestructive ? 'Confirmar exclusão' : 'Confirmar'}
        </button>
        <button
          type="button"
          onClick={() => setState({ kind: 'canceled' })}
          disabled={state.kind === 'confirming'}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
            'border border-[var(--border)] text-[var(--fg-muted)] transition-colors',
            'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] hover:text-[var(--fg)]',
            'disabled:opacity-50',
          )}
        >
          <X className="h-3.5 w-3.5" />
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabecalhos por kind
// ---------------------------------------------------------------------------

const HEADER: Record<
  AssistantProposedAction['action'],
  { icon: ReactNode; title: string }
> = {
  create_client: {
    icon: <UserPlus className="h-3.5 w-3.5 text-[var(--accent)]" />,
    title: 'Confirmar cadastro de cliente',
  },
  create_financial_entry: {
    icon: <Wallet className="h-3.5 w-3.5 text-[var(--accent)]" />,
    title: 'Confirmar lançamento financeiro',
  },
  create_financial_category: {
    icon: <Tag className="h-3.5 w-3.5 text-[var(--accent)]" />,
    title: 'Confirmar categoria financeira',
  },
  delete_financial_entry: {
    icon: <Trash2 className="h-3.5 w-3.5 text-red-600 dark:text-red-500" />,
    title: 'Confirmar exclusão de lançamento',
  },
  generate_document: {
    icon: <FilePlus2 className="h-3.5 w-3.5 text-[var(--accent)]" />,
    title: 'Confirmar geração de documento',
  },
  create_document_template: {
    icon: <FileSignature className="h-3.5 w-3.5 text-[var(--accent)]" />,
    title: 'Confirmar novo modelo (rascunho)',
  },
  create_case: {
    icon: <Scale className="h-3.5 w-3.5 text-[var(--accent)]" />,
    title: 'Confirmar cadastro de processo',
  },
  create_task: {
    icon: <ListTodo className="h-3.5 w-3.5 text-[var(--accent)]" />,
    title: 'Confirmar tarefa/prazo',
  },
};

/** Mensagem do estado "cancelado" por kind (usuario clicou Cancelar). */
const CANCELED_LABEL: Record<AssistantProposedAction['action'], string> = {
  create_client: 'Cadastro cancelado.',
  create_financial_entry: 'Lançamento cancelado.',
  create_financial_category: 'Categoria cancelada.',
  delete_financial_entry: 'Exclusão cancelada. O lançamento não foi alterado.',
  generate_document: 'Geração cancelada. Nenhum documento foi criado.',
  create_document_template: 'Modelo descartado. Nada foi salvo.',
  create_case: 'Cadastro de processo cancelado.',
  create_task: 'Tarefa cancelada.',
};

// ---------------------------------------------------------------------------
// Preview por kind
// ---------------------------------------------------------------------------

function renderPreview(action: AssistantProposedAction): ReactNode {
  switch (action.action) {
    case 'create_client':
      return <ClientPreview preview={action.preview} />;
    case 'create_financial_entry':
      return <FinancialPreview preview={action.preview} />;
    case 'create_financial_category':
      return <CategoryPreview preview={action.preview} />;
    case 'delete_financial_entry':
      return <DeleteFinancialEntryPreviewView preview={action.preview} />;
    case 'generate_document':
      return <GenerateDocumentPreviewView preview={action.preview} />;
    case 'create_document_template':
      return <CreateDocumentTemplatePreviewView preview={action.preview} />;
    case 'create_case':
      return <CasePreview preview={action.preview} />;
    case 'create_task':
      return <TaskPreview preview={action.preview} />;
  }
}

const CLIENT_TYPE_LABEL: Record<string, string> = {
  pf: 'Pessoa física',
  pj: 'Pessoa jurídica',
};
const CLIENT_STATUS_LABEL: Record<string, string> = {
  lead: 'Lead',
  consultation_paid: 'Consulta paga',
  active: 'Ativo',
  inactive: 'Inativo',
};
const CONTACT_LABEL: Record<string, string> = {
  phone: 'Telefone',
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  address: 'Endereço',
};

function ClientPreview({ preview }: { preview: AssistantCreateClientPreview }) {
  return (
    <>
      <Row label="Nome" value={preview.name} />
      <Row label="Tipo" value={CLIENT_TYPE_LABEL[preview.type] ?? preview.type} />
      {preview.document && (
        <Row label="Documento" value={formatDoc(preview.document, preview.type)} />
      )}
      <Row label="Situação" value={CLIENT_STATUS_LABEL[preview.status] ?? preview.status} />
      {preview.primaryContact && (
        <Row
          label={CONTACT_LABEL[preview.primaryContact.type] ?? 'Contato'}
          value={preview.primaryContact.value}
        />
      )}
      {preview.contactsCount > 1 && (
        <p className="text-[var(--fg-muted)] italic">
          +{preview.contactsCount - 1} contato(s) adicional(is)
        </p>
      )}
    </>
  );
}

function FinancialPreview({ preview }: { preview: AssistantFinancialEntryPreview }) {
  const isIncome = preview.type === 'income';
  return (
    <>
      <div className="flex gap-2">
        <dt className="w-24 shrink-0 text-[var(--fg-muted)]">Tipo</dt>
        <dd
          className={cn(
            'flex-1 inline-flex items-center gap-1 font-medium',
            isIncome ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500',
          )}
        >
          {isIncome ? (
            <ArrowUpCircle className="h-3.5 w-3.5" />
          ) : (
            <ArrowDownCircle className="h-3.5 w-3.5" />
          )}
          {isIncome ? 'Receita' : 'Despesa'}
        </dd>
      </div>
      <Row label="Valor" value={formatBRL(preview.amount)} strong wide />
      <Row label="Descrição" value={preview.description} wide />
      <Row label="Vencimento" value={formatDateBR(preview.dueDate)} wide />
      {preview.paymentDate && (
        <Row label="Pago em" value={formatDateBR(preview.paymentDate)} wide />
      )}
      <Row label="Conta" value={preview.accountName} wide />
      {preview.categoryName && <Row label="Categoria" value={preview.categoryName} wide />}
      {(preview.hasClient || preview.hasCase) && (
        <p className="text-[var(--fg-muted)] italic">
          Vinculado a {preview.hasClient ? 'cliente' : ''}
          {preview.hasClient && preview.hasCase ? ' e ' : ''}
          {preview.hasCase ? 'processo' : ''}.
        </p>
      )}
    </>
  );
}

function CategoryPreview({ preview }: { preview: AssistantFinancialCategoryPreview }) {
  const isIncome = preview.type === 'income';
  return (
    <>
      <Row label="Nome" value={preview.name} strong wide />
      <div className="flex gap-2">
        <dt className="w-24 shrink-0 text-[var(--fg-muted)]">Tipo</dt>
        <dd
          className={cn(
            'flex-1 inline-flex items-center gap-1 font-medium',
            isIncome ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500',
          )}
        >
          {isIncome ? (
            <ArrowUpCircle className="h-3.5 w-3.5" />
          ) : (
            <ArrowDownCircle className="h-3.5 w-3.5" />
          )}
          {isIncome ? 'Receita' : 'Despesa'}
        </dd>
      </div>
      {preview.color && (
        <div className="flex gap-2">
          <dt className="w-24 shrink-0 text-[var(--fg-muted)]">Cor</dt>
          <dd className="flex-1 inline-flex items-center gap-1.5 text-[var(--fg)]">
            <span
              className="h-3 w-3 shrink-0 rounded-full border border-[var(--border)]"
              style={{ backgroundColor: preview.color }}
            />
            {preview.color}
          </dd>
        </div>
      )}
    </>
  );
}

const DELETE_ENTRY_TYPE_LABEL: Record<string, string> = {
  income: 'Receita',
  expense: 'Despesa',
  transfer: 'Transferência',
};

function DeleteFinancialEntryPreviewView({
  preview,
}: {
  preview: AssistantDeleteFinancialEntryPreview;
}) {
  return (
    <>
      <Row label="Descrição" value={preview.description} strong wide />
      <Row label="Valor" value={formatBRL(preview.amount)} strong wide />
      <Row label="Tipo" value={DELETE_ENTRY_TYPE_LABEL[preview.type] ?? preview.type} wide />
      <Row label="Vencimento" value={formatDateBR(preview.dueDate)} wide />
      {preview.clientName && <Row label="Cliente" value={preview.clientName} wide />}
      {preview.isTransfer && (
        <p className="text-red-700 dark:text-red-400">
          Este lançamento faz parte de uma transferência entre contas: excluir remove as DUAS
          pontas vinculadas.
        </p>
      )}
    </>
  );
}

/** Ate quantas variaveis em branco listar antes de resumir em "+N". */
const MAX_MISSING_SHOWN = 5;

function GenerateDocumentPreviewView({
  preview,
}: {
  preview: AssistantGenerateDocumentPreview;
}) {
  const shown = preview.missingLabels.slice(0, MAX_MISSING_SHOWN);
  const rest = preview.missingCount - shown.length;

  return (
    <>
      <Row label="Modelo" value={preview.templateName} strong wide />
      {preview.clientName && <Row label="Cliente" value={preview.clientName} wide />}
      {preview.caseNumber && <Row label="Processo" value={preview.caseNumber} wide />}
      <Row
        label="Variáveis"
        value={
          preview.totalVariables === 0
            ? 'Este modelo não usa variáveis'
            : `${preview.filledCount} de ${preview.totalVariables} preenchidas`
        }
        wide
      />
      {preview.missingCount > 0 && (
        <div className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>
            {preview.missingCount === 1
              ? 'Uma variável ficará em branco'
              : `${preview.missingCount} variáveis ficarão em branco`}{' '}
            (marcadas com ___ no documento): {shown.join(', ')}
            {rest > 0 ? ` e mais ${rest}` : ''}. Dá para preencher no editor depois.
          </span>
        </div>
      )}
    </>
  );
}

/** Ate quantos rotulos de variavel listar antes de resumir em "+N". */
const MAX_TEMPLATE_VARIABLES_SHOWN = 6;

/**
 * Preview de um MODELO redigido pela IA. Mostra o suficiente para o usuario
 * reconhecer a peca (nome, tamanho, variaveis, comeco do texto) e NAO o modelo
 * inteiro — quem le e ajusta o texto e o editor, depois de confirmar.
 *
 * O aviso de rascunho e fixo, nao condicional: todo modelo juridico redigido
 * por IA precisa de revisao do advogado antes de virar peca do escritorio.
 */
function CreateDocumentTemplatePreviewView({
  preview,
}: {
  preview: AssistantCreateDocumentTemplatePreview;
}) {
  const shown = preview.variableLabels.slice(0, MAX_TEMPLATE_VARIABLES_SHOWN);
  const rest = preview.variableCount - shown.length;

  return (
    <>
      <Row label="Modelo" value={preview.name} strong wide />
      {preview.categoryName && <Row label="Categoria" value={preview.categoryName} wide />}
      <Row
        label="Tamanho"
        value={
          preview.paragraphCount === 1
            ? '1 parágrafo'
            : `${preview.paragraphCount} parágrafos`
        }
        wide
      />
      <Row
        label="Variáveis"
        value={
          preview.variableCount === 0
            ? 'Nenhuma (texto fixo)'
            : `${shown.join(', ')}${rest > 0 ? ` e mais ${rest}` : ''}`
        }
        wide
      />

      <div className="rounded border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-2 py-1.5">
        <div className="mb-0.5 text-[var(--fg-muted)]">Trecho inicial</div>
        <p className="break-words italic text-[var(--fg)]">{preview.excerpt}</p>
      </div>

      <div className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
        <span>
          Rascunho gerado por IA. Revise o texto, a fundamentação e os prazos no editor antes de
          usar este modelo em qualquer peça.
        </span>
      </div>
    </>
  );
}

const CASE_PARTY_ROLE_LABEL: Record<string, string> = {
  autor: 'Autor',
  reu: 'Réu',
  terceiro_interessado: 'Terceiro interessado',
  requerente: 'Requerente',
  requerido: 'Requerido',
  interessado: 'Interessado',
};

function CasePreview({ preview }: { preview: AssistantCreateCasePreview }) {
  return (
    <>
      <Row label="Processo" value={preview.numeroCnjMascara} strong wide />
      <Row
        label="Cliente"
        value={`${preview.clientName} (${CASE_PARTY_ROLE_LABEL[preview.clientRole] ?? preview.clientRole})`}
        wide
      />
      <Row
        label="Tipo de ação"
        value={
          preview.actionGroupName
            ? `${preview.actionTypeName} · ${preview.actionGroupName}`
            : preview.actionTypeName
        }
        wide
      />
      {preview.tribunalCode && <Row label="Tribunal" value={preview.tribunalCode} wide />}
      {preview.vara && <Row label="Vara" value={preview.vara} wide />}
      {preview.openedAt && (
        <Row label="Ajuizamento" value={formatDateBR(preview.openedAt)} wide />
      )}
      {preview.valorCausa != null && (
        <Row label="Valor da causa" value={formatBRL(preview.valorCausa)} wide />
      )}
      {preview.dataJudFound ? (
        <div className="flex items-start gap-1.5 rounded border border-green-200 bg-green-50 px-2 py-1.5 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-400">
          <Check className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>Tribunal, vara e dados do processo confirmados no DataJud.</span>
        </div>
      ) : (
        <div className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>
            Processo não encontrado no DataJud — tribunal e vara informados manualmente. Confira
            antes de gravar.
          </span>
        </div>
      )}
    </>
  );
}

const TASK_PRIORITY_LABEL: Record<string, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

function TaskPreview({ preview }: { preview: AssistantCreateTaskPreview }) {
  const flags = [preview.isImportant && 'Importante', preview.isUrgent && 'Urgente']
    .filter((v): v is string => !!v)
    .join(' · ');
  return (
    <>
      <Row label="Título" value={preview.title} strong wide />
      {preview.description && <Row label="Descrição" value={preview.description} wide />}
      {preview.dueDate && (
        <Row
          label="Vencimento"
          value={`${formatDateBR(preview.dueDate)}${preview.dueTime ? ` às ${preview.dueTime}` : ''}`}
          wide
        />
      )}
      {preview.fatalDate && (
        <Row label="Prazo fatal" value={formatDateBR(preview.fatalDate)} wide />
      )}
      <Row label="Prioridade" value={TASK_PRIORITY_LABEL[preview.priority] ?? preview.priority} wide />
      <Row label="Responsável" value={preview.assigneeName} wide />
      {preview.categoryName && <Row label="Categoria" value={preview.categoryName} wide />}
      {preview.clientName && <Row label="Cliente" value={preview.clientName} wide />}
      {preview.caseNumber && <Row label="Processo" value={preview.caseNumber} wide />}
      {flags && <p className="text-[var(--fg-muted)] italic">{flags}</p>}
    </>
  );
}

// ---------------------------------------------------------------------------
// Estado terminal (feito) por kind
// ---------------------------------------------------------------------------

function renderDone(result: AssistantConfirmActionResponse): ReactNode {
  const shell = (children: ReactNode) => (
    <div className="rounded-lg border border-[color-mix(in_oklch,#16a34a_40%,var(--border))] bg-[color-mix(in_oklch,#16a34a_10%,transparent)] px-3 py-2.5 text-xs">
      {children}
    </div>
  );
  // Shell neutro para exclusao: nao e uma "conquista" para celebrar em verde,
  // e um registro de que a acao destrutiva foi efetivada.
  const deletedShell = (children: ReactNode) => (
    <div className="rounded-lg border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_5%,transparent)] px-3 py-2.5 text-xs">
      {children}
    </div>
  );

  switch (result.action) {
    case 'create_client':
      return shell(
        <>
          <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
            <Check className="h-3.5 w-3.5 text-green-600" />
            Cliente cadastrado
          </div>
          <Link
            to={`/escritorio/clientes/${result.client.id}`}
            className="mt-1 inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
          >
            {result.client.name}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </>,
      );
    case 'create_financial_entry':
      return shell(
        <>
          <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
            <Check className="h-3.5 w-3.5 text-green-600" />
            Lançamento registrado
          </div>
          <Link
            to={`/escritorio/financeiro/${result.transaction.id}`}
            className="mt-1 inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
          >
            {result.transaction.description} · {formatBRL(result.transaction.amount)}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </>,
      );
    case 'create_financial_category':
      return shell(
        <>
          <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
            <Check className="h-3.5 w-3.5 text-green-600" />
            Categoria criada
          </div>
          <Link
            to="/escritorio/configuracoes/financeiro"
            className="mt-1 inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
          >
            {result.category.name}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </>,
      );
    case 'delete_financial_entry':
      return deletedShell(
        <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
          <Trash2 className="h-3.5 w-3.5 text-[var(--fg-muted)]" />
          Lançamento excluído: {result.transaction.description} ·{' '}
          {formatBRL(result.transaction.amount)}
        </div>,
      );
    case 'generate_document':
      // Card com as DUAS acoes que o usuario pediu ao assistente: abrir no
      // editor (editorPath vem pronto do backend) e baixar o PDF (download
      // autenticado — ver GeneratedDocumentDone).
      return shell(<GeneratedDocumentDone document={result.document} />);
    case 'create_document_template':
      // Estado "feito" com o aviso de rascunho REPETIDO: o card de confirmacao
      // some depois de confirmar, e este e o unico texto que fica na conversa.
      // O caminho natural aqui e abrir o editor para revisar, nao "usar".
      return shell(
        <>
          <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
            <Check className="h-3.5 w-3.5 text-green-600" />
            Modelo criado (rascunho)
          </div>
          <p className="mt-1 break-words text-[var(--fg)]">
            {result.template.name}
            {result.template.categoryName ? ` · ${result.template.categoryName}` : ''}
          </p>
          <p className="mt-1 text-[var(--fg-muted)]">
            Revise o texto e a fundamentação no editor antes de usar.
          </p>
          <Link
            to={result.template.editorPath}
            className={cn(
              'mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium',
              'border border-[var(--border)] text-[var(--fg)] transition-colors',
              'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
            )}
          >
            <FileText className="h-3.5 w-3.5 shrink-0" />
            Abrir no editor
          </Link>
        </>,
      );
    case 'create_case':
      return shell(
        <>
          <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
            <Check className="h-3.5 w-3.5 text-green-600" />
            Processo cadastrado
          </div>
          <Link
            to={`/escritorio/processos/${result.case.id}`}
            className="mt-1 inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
          >
            {result.case.numeroCnj ?? 'Ver processo'}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </>,
      );
    case 'create_task':
      return shell(
        <>
          <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
            <Check className="h-3.5 w-3.5 text-green-600" />
            Tarefa criada
          </div>
          <Link
            to={`/escritorio/atividades?taskId=${encodeURIComponent(result.task.id)}`}
            className="mt-1 inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
          >
            {result.task.title}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </>,
      );
  }
}

// ---------------------------------------------------------------------------
// Estado terminal de generate_document: abrir editor + baixar PDF
// ---------------------------------------------------------------------------

type PdfDownloadState = 'idle' | 'downloading' | 'error';

/**
 * Acoes do card "documento gerado": abrir no editor (link de rota da SPA,
 * `editorPath` pronto do backend) e baixar o PDF. O download PRECISA de
 * fetch autenticado (GET /api/documents/instances/:id/pdf exige Bearer) —
 * um `<a href>` direto nao levaria o header, por isso passa por
 * `apiClient.downloadDocumentInstancePdf` (Blob + filename do
 * Content-Disposition) e dispara o download via link temporario em memoria.
 */
function GeneratedDocumentDone({ document: doc }: { document: AssistantGeneratedDocumentResult }) {
  const [pdfState, setPdfState] = useState<PdfDownloadState>('idle');
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function handleDownloadPdf() {
    setPdfState('downloading');
    setPdfError(null);
    try {
      const { blob, filename } = await apiClient.downloadDocumentInstancePdf(doc.id);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setPdfState('idle');
    } catch (err) {
      setPdfState('error');
      setPdfError(describeError(err));
    }
  }

  return (
    <>
      <div className="flex items-center gap-1.5 font-medium text-[var(--fg)]">
        <Check className="h-3.5 w-3.5 text-green-600" />
        Documento gerado
      </div>
      <p className="mt-1 break-words text-[var(--fg)]">{doc.title}</p>

      {doc.missingCount > 0 && (
        <p className="mt-1 text-[var(--fg-muted)]">
          {doc.missingCount === 1
            ? 'Uma variável ficou em branco (___)'
            : `${doc.missingCount} variáveis ficaram em branco (___)`}
          . Complete no editor.
        </p>
      )}

      {pdfState === 'error' && pdfError && (
        <div className="mt-2 flex items-start gap-1.5 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
          <span>{pdfError}</span>
        </div>
      )}

      <div className="mt-2 flex gap-2">
        <Link
          to={doc.editorPath}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium',
            'border border-[var(--border)] text-[var(--fg)] transition-colors',
            'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
          )}
        >
          <FileText className="h-3.5 w-3.5 shrink-0" />
          Abrir no editor
        </Link>
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={pdfState === 'downloading'}
          className={cn(
            'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 font-medium',
            'bg-[var(--accent)] text-[var(--accent-fg)] transition-opacity hover:opacity-90 disabled:opacity-50',
          )}
        >
          {pdfState === 'downloading' ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5 shrink-0" />
          )}
          Baixar PDF
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers de apresentacao
// ---------------------------------------------------------------------------

function Row({
  label,
  value,
  strong,
  wide,
}: {
  label: string;
  value: string;
  strong?: boolean;
  wide?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <dt className={cn('shrink-0 text-[var(--fg-muted)]', wide ? 'w-24' : 'w-20')}>{label}</dt>
      <dd
        className={cn(
          'flex-1 break-words',
          strong ? 'font-semibold text-[var(--fg)]' : 'text-[var(--fg)]',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
function formatBRL(amount: number): string {
  return BRL.format(amount);
}

/** Formata 'YYYY-MM-DD' -> 'DD/MM/YYYY' sem passar por Date (evita shift de TZ). */
function formatDateBR(iso: string): string {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/** Formata CPF/CNPJ (so digitos -> mascara) para exibicao. */
function formatDoc(digits: string, type: string): string {
  const d = digits.replace(/\D/g, '');
  if (type === 'pf' && d.length === 11) {
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  if (type === 'pj' && d.length === 14) {
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }
  return digits;
}

/**
 * Traduz o erro do confirm em mensagem util. Distingue casos comuns pelo status
 * do ApiError.
 */
function describeError(err: unknown): string {
  const apiErr = err as Partial<ApiError> | undefined;
  const status = apiErr?.status;
  if (status === 404) {
    return 'A proposta expirou ou já foi usada. Peça novamente ao assistente.';
  }
  if (status === 403) {
    return 'Você não tem permissão para confirmar esta ação.';
  }
  if (status === 409) {
    return apiErr?.message ?? 'Já existe um registro com estes dados.';
  }
  if (typeof apiErr?.message === 'string' && apiErr.message) {
    return apiErr.message;
  }
  return 'Não foi possível concluir. Tente novamente.';
}
